import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { HelpCircle } from 'lucide-react'
import { ErrorBoundary } from '@/components/app/ErrorBoundary'
import { Onboarding } from '@/components/app/Onboarding'
import { isRTL } from '@/utils/i18n'
import { EmptyState } from '@/components/app/EmptyState'
import { usePdfStore } from '@/store/usePdfStore'
import {
  addRecentFile, loadRecentFileFull, updateRecentFile,
} from '@/utils/recentFiles'
// `mergePdf` and `reorderPdfPages` both pull in pdf-lib (~700KB minified).
// They're only used when the user clicks Merge or drags a thumbnail, so
// import them dynamically below to keep the initial bundle small. Same
// pattern as `buildPdf` in handleDownload.
import { formatDate } from '@/utils/dateFormats'
import { useT } from '@/utils/useT'
import { PDFJS_ASSET_OPTIONS } from '@/utils/pdfjsAssets'
import type { PDFPageProxy } from 'pdfjs-dist'
import type { Annotation, CompressionLevel, FontFamily, PageInfo, RedactionAnnotation } from '@/types'

const AUTO_SAVE_DEBOUNCE_MS = 800

const Toolbar = lazy(() => import('@/components/app/Toolbar').then((m) => ({ default: m.Toolbar })))
const ModeBanner = lazy(() => import('@/components/app/ModeBanner').then((m) => ({ default: m.ModeBanner })))
const RecentSidebar = lazy(() => import('@/components/app/RecentSidebar').then((m) => ({ default: m.RecentSidebar })))
const PdfViewer = lazy(() => import('@/components/app/PdfViewer').then((m) => ({ default: m.PdfViewer })))
const SignatureModal = lazy(() => import('@/components/app/SignatureModal').then((m) => ({ default: m.SignatureModal })))
const ProfileDialog = lazy(() => import('@/components/app/ProfileDialog').then((m) => ({ default: m.ProfileDialog })))
const PdfThumbnailRail = lazy(() => import('@/components/app/PdfThumbnailRail').then((m) => ({ default: m.PdfThumbnailRail })))
const LanguagePicker = lazy(() => import('@/components/app/LanguagePicker').then((m) => ({ default: m.LanguagePicker })))
const ThemeToggle = lazy(() => import('@/components/app/ThemeToggle').then((m) => ({ default: m.ThemeToggle })))
const PdfPagesDialog = lazy(() => import('@/components/app/PdfThumbnailRail').then((m) => ({ default: m.PdfPagesDialog })))

type CompressionPreset = {
  dpi: number
  jpeg: number
}

type CanvasOverlayPainter = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => void

const COMPRESSION_PRESETS: Record<CompressionLevel, CompressionPreset> = {
  // Low compression keeps more visual detail.
  low: { dpi: 200, jpeg: 0.85 },
  // Mid is the default sharing/printing compromise.
  mid: { dpi: 150, jpeg: 0.75 },
  // High compression aims for the smallest file.
  high: { dpi: 96, jpeg: 0.6 },
}

async function renderPdfPageToJpeg(
  page: PDFPageProxy,
  preset: CompressionPreset,
  paintOverlays?: CanvasOverlayPainter,
): Promise<string> {
  const scale = preset.dpi / 72
  const vp = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(vp.width)
  canvas.height = Math.floor(vp.height)
  try {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not allocate canvas context')
    await page.render({ canvasContext: ctx, viewport: vp, canvas }).promise
    paintOverlays?.(ctx, canvas)
    return canvas.toDataURL('image/jpeg', preset.jpeg)
  } finally {
    // Release the backing store promptly on large multi-page documents.
    canvas.width = 0
    canvas.height = 0
  }
}

async function renderPdfBytesToJpegs(
  bytes: Uint8Array,
  expectedPageCount: number,
  preset: CompressionPreset,
): Promise<string[]> {
  const [pdfjsLib, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ])
  pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default
  const task = pdfjsLib.getDocument({ data: bytes.slice(), ...PDFJS_ASSET_OPTIONS })
  const doc = await task.promise
  try {
    if (doc.numPages !== expectedPageCount) {
      throw new Error(`Compressed render page-count mismatch: expected ${expectedPageCount}, got ${doc.numPages}`)
    }
    const pageImages: string[] = []
    for (let pageNo = 1; pageNo <= doc.numPages; pageNo++) {
      const page = await doc.getPage(pageNo)
      pageImages.push(await renderPdfPageToJpeg(page, preset))
      try { page.cleanup() } catch { /* noop */ }
    }
    return pageImages
  } finally {
    await task.destroy()
  }
}

// Render only `pageIdxs` (0-based) of a PDF to JPEG data URLs.
async function renderPdfBytesPagesToJpegs(
  bytes: Uint8Array,
  pageIdxs: number[],
  preset: CompressionPreset,
): Promise<Map<number, string>> {
  const [pdfjsLib, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ])
  pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default
  const task = pdfjsLib.getDocument({ data: bytes.slice(), ...PDFJS_ASSET_OPTIONS })
  const doc = await task.promise
  try {
    const out = new Map<number, string>()
    for (const idx of pageIdxs) {
      const page = await doc.getPage(idx + 1)
      out.set(idx, await renderPdfPageToJpeg(page, preset))
      try { page.cleanup() } catch { /* noop */ }
    }
    return out
  } finally {
    await task.destroy()
  }
}

// "Edit text" only paints over the original glyphs, which stay extractable
// in the file. Rasterise just the pages carrying text edits so the old text
// is really gone; every other page stays vector.
async function rasterizeTextEditPages(bytes: Uint8Array, annotations: Annotation[]): Promise<Uint8Array> {
  const pageIdxs = [...new Set(annotations.filter((a) => a.type === 'textEdit').map((a) => a.pageIdx))]
  if (pageIdxs.length === 0) return bytes
  const images = await renderPdfBytesPagesToJpegs(bytes, pageIdxs, COMPRESSION_PRESETS.low)
  const { replacePagesWithImages } = await import('@/utils/replacePagesWithImages')
  return replacePagesWithImages(bytes, images)
}

async function renderLoadedPagesToJpegs(
  pages: PDFPageProxy[],
  expectedPageCount: number,
  preset: CompressionPreset,
  opts: { bakeLiveFormFields?: boolean } = {},
): Promise<string[]> {
  if (pages.length !== expectedPageCount) {
    throw new Error(`Compressed render page-count mismatch: expected ${expectedPageCount}, got ${pages.length}`)
  }
  const pageImages: string[] = []
  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const page = pages[pageIdx]
    pageImages.push(await renderPdfPageToJpeg(
      page,
      preset,
      opts.bakeLiveFormFields
        ? (ctx, canvas) => drawLiveFormFieldsOntoCanvas(ctx, canvas, pageIdx)
        : undefined,
    ))
  }
  return pageImages
}

function drawLiveFormFieldsOntoCanvas(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  pageIdx: number,
) {
  const pageEl = document.querySelector<HTMLElement>(`[data-page-idx="${pageIdx}"]`)
  if (!pageEl) return
  const pageRect = pageEl.getBoundingClientRect()
  if (pageRect.width <= 0 || pageRect.height <= 0) return
  const scaleX = canvas.width / pageRect.width
  const scaleY = canvas.height / pageRect.height
  const fields = pageEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-form-field-name]')
  if (fields.length === 0) return

  ctx.save()
  for (const el of Array.from(fields)) {
    const rect = el.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) continue
    const x = (rect.left - pageRect.left) * scaleX
    const y = (rect.top - pageRect.top) * scaleY
    const w = rect.width * scaleX
    const h = rect.height * scaleY

    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      if (!el.checked) continue
      const pad = Math.min(w, h) * 0.22
      ctx.strokeStyle = '#0a1f3d'
      ctx.lineWidth = Math.max(1.5, Math.min(w, h) * 0.12)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.beginPath()
      ctx.moveTo(x + pad, y + h * 0.52)
      ctx.lineTo(x + w * 0.42, y + h - pad)
      ctx.lineTo(x + w - pad, y + pad)
      ctx.stroke()
      continue
    }

    const value = el.value
    if (!value) continue
    const style = window.getComputedStyle(el)
    const cssFontSize = Number.parseFloat(style.fontSize) || Math.min(rect.height * 0.7, 14)
    const fontSize = Math.max(6, cssFontSize * scaleY)
    const lineHeight = fontSize * 1.2
    const padX = Math.max(2, Number.parseFloat(style.paddingLeft) * scaleX || 4 * scaleX)
    const padY = Math.max(1, Number.parseFloat(style.paddingTop) * scaleY || 2 * scaleY)

    ctx.save()
    ctx.beginPath()
    ctx.rect(x, y, w, h)
    ctx.clip()
    ctx.fillStyle = style.color || '#0a1f3d'
    ctx.font = `${style.fontStyle || 'normal'} ${style.fontWeight || '400'} ${fontSize}px ${style.fontFamily || 'Helvetica, Arial, sans-serif'}`
    ctx.textBaseline = el instanceof HTMLTextAreaElement ? 'top' : 'middle'
    const lines = value.split(/\r?\n/)
    if (el instanceof HTMLTextAreaElement) {
      lines.forEach((line, idx) => {
        ctx.fillText(line, x + padX, y + padY + idx * lineHeight)
      })
    } else {
      ctx.fillText(lines[0] ?? '', x + padX, y + h / 2)
    }
    ctx.restore()
  }
  ctx.restore()
}

async function burnRedactionsIntoPageImages(
  pageImages: string[],
  pages: PageInfo[],
  redactions: RedactionAnnotation[],
  jpegQuality: number,
): Promise<string[]> {
  if (redactions.length === 0) return pageImages
  const byPage = new Map<number, RedactionAnnotation[]>()
  for (const r of redactions) {
    const list = byPage.get(r.pageIdx) ?? []
    list.push(r)
    byPage.set(r.pageIdx, list)
  }
  const out: string[] = []
  for (let pageIdx = 0; pageIdx < pageImages.length; pageIdx++) {
    const list = byPage.get(pageIdx)
    if (!list?.length) {
      out.push(pageImages[pageIdx])
      continue
    }
    const info = pages[pageIdx]
    if (!info) {
      out.push(pageImages[pageIdx])
      continue
    }
    const img = await loadImage(pageImages[pageIdx])
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width || 1
    canvas.height = img.naturalHeight || img.height || 1
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not allocate redaction canvas')
    ctx.drawImage(img, 0, 0)
    const sx = canvas.width / info.pdfWidth
    const sy = canvas.height / info.pdfHeight
    for (const r of list) {
      ctx.fillStyle = r.color || '#000000'
      ctx.fillRect(r.x * sx, r.y * sy, r.w * sx, r.h * sy)
    }
    out.push(canvas.toDataURL('image/jpeg', jpegQuality))
    canvas.width = 0
    canvas.height = 0
  }
  return out
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not decode page image for redaction'))
    img.src = dataUrl
  })
}

function runWhenIdle(fn: () => void) {
  const requestIdle = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  }).requestIdleCallback
  if (requestIdle) {
    requestIdle(fn, { timeout: 1_000 })
    return
  }
  window.setTimeout(fn, 0)
}

function AutoSaveRecentFile() {
  const recentId = usePdfStore((s) => s.recentId)
  const annotations = usePdfStore((s) => s.annotations)
  const formFieldEdits = usePdfStore((s) => s.formFieldEdits)
  const watermark = usePdfStore((s) => s.watermark)
  const pageNumbers = usePdfStore((s) => s.pageNumbers)
  const lastSavedRef = useRef<{
    id: string
    ann: Annotation[]
    ff: Map<string, string | boolean>
    wm: typeof watermark
    pn: typeof pageNumbers
  } | null>(null)
  // The debounced save that hasn't run yet. Flushed (not dropped) when the
  // user switches documents or leaves the page, so the last edit survives.
  const pendingRef = useRef<{ timer: number; save: () => void } | null>(null)

  const flush = useCallback(() => {
    const pending = pendingRef.current
    if (!pending) return
    pendingRef.current = null
    clearTimeout(pending.timer)
    pending.save()
  }, [])

  useEffect(() => {
    if (!recentId) return
    // Skip the initial call right after loadFromRecent (lastSavedRef matches).
    const last = lastSavedRef.current
    if (last && last.id === recentId && last.ann === annotations && last.ff === formFieldEdits && last.wm === watermark && last.pn === pageNumbers) return
    const save = () => {
      // Image annotations are deliberately session-only — strip before persist.
      // They live in memory and download fine, but they don't follow the PDF
      // across reloads or browser sessions.
      const persisted = annotations.filter((a) => a.type !== 'image')
      updateRecentFile(recentId, {
        annotations: persisted,
        formFieldEdits: Array.from(formFieldEdits.entries()),
        watermark,
        pageNumbers,
      }).catch(() => {})
      lastSavedRef.current = { id: recentId, ann: annotations, ff: formFieldEdits, wm: watermark, pn: pageNumbers }
    }
    // A newer edit to the same document supersedes the pending save.
    if (pendingRef.current) clearTimeout(pendingRef.current.timer)
    const timer = window.setTimeout(() => {
      pendingRef.current = null
      save()
    }, AUTO_SAVE_DEBOUNCE_MS)
    pendingRef.current = { timer, save }
  }, [recentId, annotations, formFieldEdits, watermark, pageNumbers])

  // Switching to another document: write the old one's pending edits first.
  useEffect(() => () => flush(), [recentId, flush])

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush() }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onHide)
      flush()
    }
  }, [flush])

  return null
}

function LandingHeader({ onOpenFile, onHelp }: { onOpenFile: () => void; onHelp: () => void }) {
  const t = useT()
  return (
    <header className="frosted flex h-14 items-center justify-between gap-3 border-b px-3 sm:px-4">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">PDF Helper</div>
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        {/* Let visitors fix an auto-detected language before opening a file.
            The theme follows the system by default, so phones skip it here
            to keep the app name readable; it's in the editor toolbar. */}
        <Suspense fallback={null}>
          <LanguagePicker />
          <div className="hidden sm:contents">
            <ThemeToggle />
          </div>
        </Suspense>
        <button
          type="button"
          onClick={onHelp}
          aria-label={t('tb.help')}
          className="inline-flex h-9 items-center rounded-md px-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground sm:px-3"
        >
          {/* Icon-only on phones so the app name isn't truncated. */}
          <HelpCircle className="size-4 sm:hidden" aria-hidden="true" />
          <span className="hidden sm:inline">{t('tb.help')}</span>
        </button>
        <button
          type="button"
          onClick={onOpenFile}
          className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-xs hover:bg-primary/90"
        >
          {t('tb.open')}
        </button>
      </div>
    </header>
  )
}

export default function App() {
  const t = useT()
  const pdfBytes = usePdfStore((s) => s.pdfBytes)
  const mode = usePdfStore((s) => s.mode)
  const selectedId = usePdfStore((s) => s.selectedId)
  const setMode = usePdfStore((s) => s.setMode)
  const setPdf = usePdfStore((s) => s.setPdf)
  const setRecentId = usePdfStore((s) => s.setRecentId)
  const loadFromRecent = usePdfStore((s) => s.loadFromRecent)
  const mergeIntoPdf = usePdfStore((s) => s.mergeIntoPdf)
  const reorderPages = usePdfStore((s) => s.reorderPages)
  const rotatePage = usePdfStore((s) => s.rotatePage)
  const deletePage = usePdfStore((s) => s.deletePage)
  const setSelectedId = usePdfStore((s) => s.setSelectedId)
  const removeAnnotation = usePdfStore((s) => s.removeAnnotation)
  const undo = usePdfStore((s) => s.undo)
  const redo = usePdfStore((s) => s.redo)
  const setPendingTextValue = usePdfStore((s) => s.setPendingTextValue)
  const setPendingDateMs = usePdfStore((s) => s.setPendingDateMs)

  const [textFamily, setTextFamily] = useState<FontFamily>('helvetica')
  const [textSize, setTextSize] = useState(14)
  const [textColor, setTextColor] = useState('#0a1f3d')
  const [snapEnabled, setSnapEnabled] = useState(true)
  const [sigModalOpen, setSigModalOpen] = useState(false)
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)
  const [recentRefreshKey, setRecentRefreshKey] = useState(0)
  const [pdfPages, setPdfPages] = useState<PDFPageProxy[]>([])
  const [pagesDialogOpen, setPagesDialogOpen] = useState(false)
  const [tourReplayKey, setTourReplayKey] = useState(0)
  const [recentOpen, setRecentOpen] = useState(false)
  const openSeqRef = useRef(0)

  // Mirror the current UI language onto the <html> element so the browser's
  // own RTL handling kicks in for Arabic — text reading order, scrollbars,
  // form controls all flip without us having to maintain a parallel CSS
  // direction stack. The `lang` attribute also helps screen readers and
  // hyphenation pick the right pronunciation rules per language.
  const lang = usePdfStore((s) => s.lang)
  const setLang = usePdfStore((s) => s.setLang)
  useEffect(() => {
    const html = document.documentElement
    html.lang = lang
    html.dir = isRTL(lang) ? 'rtl' : 'ltr'
  }, [lang])

  // Cross-tab sync — if the user changes the language in another tab, the
  // browser fires a `storage` event in this one. Mirror it into our store
  // so both tabs converge without a reload.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'pdfhelper.lang' || !e.newValue) return
      setLang(e.newValue as typeof lang)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [setLang, lang])

  const handleOpenFile = useCallback(async (file: File) => {
    const seq = ++openSeqRef.current
    const buf = await file.arrayBuffer()
    const bytes = new Uint8Array(buf)
    // Show the PDF immediately. Recent-file fingerprinting walks the full
    // byte array; run it after first paint so large scans don't feel stuck.
    setPdf(bytes, file.name, null)
    runWhenIdle(() => {
      void (async () => {
        const id = await addRecentFile(file.name, bytes)
        if (!id || seq !== openSeqRef.current) return
        // Try loading saved annotations for this file (if it's a duplicate of
        // one we've seen before, we restore previous edits). If the user has
        // already started editing, keep their current session and only attach
        // the recent ID so auto-save can continue from there.
        const saved = await loadRecentFileFull(id)
        if (seq !== openSeqRef.current) return
        const cur = usePdfStore.getState()
        const untouched =
          cur.pdfBytes === bytes &&
          cur.annotations.length === 0 &&
          cur.formFieldEdits.size === 0
        const hasSavedEdits = !!saved && (
          saved.annotations.length > 0 ||
          saved.formFieldEdits.length > 0 ||
          (saved.watermark.enabled && saved.watermark.text.trim().length > 0) ||
          saved.pageNumbers.enabled
        )
        if (hasSavedEdits && untouched) {
          loadFromRecent(saved.bytes, saved.name, saved.id, saved.annotations, saved.formFieldEdits, saved.watermark, saved.pageNumbers)
        } else if (cur.pdfBytes === bytes) {
          setRecentId(id)
        }
        setRecentRefreshKey((k) => k + 1)
      })()
    })
  }, [setPdf, setRecentId, loadFromRecent])

  // Page operations rewrite the whole PDF. Run them one at a time against the
  // latest bytes in the store, so a quick second click can't build on stale
  // bytes and silently undo the first operation.
  const pageOpQueueRef = useRef<Promise<void>>(Promise.resolve())
  const runPageOp = useCallback((
    op: (bytes: Uint8Array) => Promise<Uint8Array>,
    errorKey: string,
  ) => {
    pageOpQueueRef.current = pageOpQueueRef.current.then(async () => {
      const { pdfBytes: current, recentId: id, pdfPassword } = usePdfStore.getState()
      if (!current) return
      try {
        // Password-protected files are edited as a decrypted in-memory copy
        // (so the viewer doesn't ask for the password again)...
        const { decryptPdf, protectPdf } = await import('@/utils/loadPdf')
        const bytes = await op(pdfPassword ? await decryptPdf(current, pdfPassword) : current)
        // Persist the new bytes to the recent-file IDB entry so reloading
        // doesn't snap back to the previous document. Annotations get
        // auto-saved separately by the debounced effect. ...but stored at
        // rest with the same password as the original.
        if (id) {
          const stored = pdfPassword ? await protectPdf(bytes, pdfPassword) : bytes
          await updateRecentFile(id, {
            bytes: stored,
            size: stored.byteLength,
            openedAt: Date.now(),
          })
        }
      } catch (err) {
        alert(t(errorKey, { message: (err as Error).message }))
      }
    })
    return pageOpQueueRef.current
  }, [t])

  const handleMergePdf = useCallback((file: File, where: 'start' | 'end') => runPageOp(async (current) => {
    const { mergePdf } = await import('@/utils/mergePdf')
    const insertBytes = new Uint8Array(await file.arrayBuffer())
    const { bytes, insertedCount } = await mergePdf(current, insertBytes, where)
    mergeIntoPdf(bytes, where, insertedCount)
    return bytes
  }, 'err.merge_pdf'), [runPageOp, mergeIntoPdf])

  const handleReorderPages = useCallback((newOrder: number[]) => runPageOp(async (current) => {
    const { reorderPdfPages } = await import('@/utils/reorderPages')
    const bytes = await reorderPdfPages(current, newOrder)
    reorderPages(bytes, newOrder)
    return bytes
  }, 'err.reorder_pages'), [runPageOp, reorderPages])

  const handleRotatePage = useCallback((pageIdx: number, direction: 'cw' | 'ccw') => runPageOp(async (current) => {
    const { rotatePdfPage } = await import('@/utils/rotatePage')
    const { bytes, width, height } = await rotatePdfPage(current, pageIdx, direction)
    rotatePage(bytes, pageIdx, direction, width, height)
    return bytes
  }, 'err.rotate_page'), [runPageOp, rotatePage])

  const handleDeletePage = useCallback((pageIdx: number) => runPageOp(async (current) => {
    const { deletePdfPage } = await import('@/utils/deletePage')
    const bytes = await deletePdfPage(current, pageIdx)
    deletePage(bytes, pageIdx)
    return bytes
  }, 'err.delete_page'), [runPageOp, deletePage])

  const handleSwitchTo = useCallback(async (id: string) => {
    const rec = await loadRecentFileFull(id)
    if (!rec) return
    loadFromRecent(rec.bytes, rec.name, rec.id, rec.annotations, rec.formFieldEdits, rec.watermark, rec.pageNumbers)
    setRecentRefreshKey((k) => k + 1)
  }, [loadFromRecent])

  const pickFile = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/pdf'
    input.onchange = () => { if (input.files?.[0]) handleOpenFile(input.files[0]) }
    input.click()
  }, [handleOpenFile])

  const handleDownload = useCallback(async (
    opts: { compress?: boolean; level?: CompressionLevel } = {},
  ) => {
    const {
      pdfBytes: currentPdfBytes,
      annotations,
      pages,
      formFieldEdits,
      watermark,
      pageNumbers,
      fileName: currentFileName,
      pdfPassword,
    } = usePdfStore.getState()
    if (!currentPdfBytes) return
    if (pages.length === 0) {
      alert(t('err.pdf_loading'))
      return
    }
    const { compress = false, level = 'mid' } = opts
    // Lazy-load buildPdf and its heavy deps (pdf-lib + fontkit) — they're
    // only needed at download time, so they shouldn't be in the initial bundle.
    const { buildPdf } = await import('@/utils/buildPdf')
    const redactions = annotations.filter((a): a is RedactionAnnotation => a.type === 'redaction')
    const annotationsWithoutRedactions = annotations.filter((a) => a.type !== 'redaction')

    if (redactions.length > 0) {
      const preset = compress ? COMPRESSION_PRESETS[level] : COMPRESSION_PRESETS.low
      try {
        // Redaction output is deliberately raster-flattened. A vector black
        // rectangle over source PDF text is only cosmetic; rasterising after
        // the edit pass removes the selectable source text from the saved PDF.
        const editedWithoutRedactions = await buildPdf({
          pdfBytes: currentPdfBytes, password: pdfPassword,
          annotations: annotationsWithoutRedactions,
          pages,
          formFieldEdits,
          watermark,
          pageNumbers,
        })
        const pageImages = await renderPdfBytesToJpegs(editedWithoutRedactions, pages.length, preset)
        const redactedImages = await burnRedactionsIntoPageImages(pageImages, pages, redactions, preset.jpeg)
        const redacted = await buildPdf({
          pdfBytes: editedWithoutRedactions,
          annotations: [],
          pages,
          formFieldEdits: new Map(),
          pageImages: redactedImages,
        })
        await triggerDownload(redacted)
        return
      } catch (err) {
        console.warn('redacted download failed, trying source-page raster fallback:', err)
        try {
          if (pdfPages.length === 0) throw new Error(t('err.pdf_loading'), { cause: err })
          const pageImages = await renderLoadedPagesToJpegs(pdfPages, pages.length, preset, {
            bakeLiveFormFields: true,
          })
          const editedFallback = await buildPdf({
            pdfBytes: currentPdfBytes, password: pdfPassword,
            annotations: annotationsWithoutRedactions,
            pages,
            formFieldEdits: new Map(),
            watermark,
            pageNumbers,
            pageImages,
          })
          const editedImages = await renderPdfBytesToJpegs(editedFallback, pages.length, preset)
          const redactedImages = await burnRedactionsIntoPageImages(editedImages, pages, redactions, preset.jpeg)
          const redacted = await buildPdf({
            pdfBytes: editedFallback,
            annotations: [],
            pages,
            formFieldEdits: new Map(),
            pageImages: redactedImages,
          })
          await triggerDownload(redacted)
          return
        } catch (fallbackErr) {
          console.error('redacted download failed', fallbackErr)
          alert(t('err.redacted_pdf', { message: (fallbackErr as Error).message }))
          return
        }
      }
    }

    if (compress) {
      if (pdfPages.length === 0) {
        alert(t('err.pdf_loading'))
        return
      }
      try {
        const preset = COMPRESSION_PRESETS[level]
        // First build the normal edited PDF, then rasterise that output.
        // This preserves annotations and filled AcroForm widgets before
        // converting the document into JPEG-backed pages.
        const edited = await rasterizeTextEditPages(
          await buildPdf({ pdfBytes: currentPdfBytes, password: pdfPassword, annotations, pages, formFieldEdits, watermark, pageNumbers }),
          annotations,
        )
        const pageImages = await renderPdfBytesToJpegs(edited, pages.length, preset)
        const compressed = await buildPdf({
          pdfBytes: edited,
          annotations: [],
          pages,
          formFieldEdits: new Map(),
          pageImages,
        })
        await triggerDownload(compressed.byteLength < edited.byteLength ? compressed : edited)
        return
      } catch (err) {
        console.warn('edited compressed download failed, trying source-page raster fallback:', err)
        try {
          const preset = COMPRESSION_PRESETS[level]
          const pageImages = await renderLoadedPagesToJpegs(pdfPages, pages.length, preset, {
            bakeLiveFormFields: true,
          })
          const compressed = await buildPdf({
            pdfBytes: currentPdfBytes, password: pdfPassword,
            annotations,
            pages,
            formFieldEdits: new Map(),
            watermark,
            pageNumbers,
            pageImages,
          })
          await triggerDownload(compressed)
          return
        } catch (fallbackErr) {
          console.error('compressed download failed', fallbackErr)
          alert(t('err.compressed_pdf', { message: (fallbackErr as Error).message }))
          return
        }
      }
    }

    try {
      const out = await buildPdf({ pdfBytes: currentPdfBytes, password: pdfPassword, annotations, pages, formFieldEdits, watermark, pageNumbers })
      await triggerDownload(await rasterizeTextEditPages(out, annotations))
    } catch (err) {
      console.warn('strict buildPdf failed, falling back to raster:', err)
      // Some real-world PDFs (encrypted-flagged government forms with
      // unusual cross-reference structures) defeat pdf-lib's parser. Render
      // every page with pdf.js (not the on-screen canvases: pages the user
      // never scrolled to are unpainted), bake live form-field overlays into
      // those images, and embed them in a fresh PDF with annotations on top.
      try {
        if (pdfPages.length === 0) throw new Error(t('err.pdf_loading'), { cause: err })
        const pageImages = await renderLoadedPagesToJpegs(pdfPages, pages.length, COMPRESSION_PRESETS.low, {
          bakeLiveFormFields: true,
        })
        const out = await buildPdf({
          pdfBytes: currentPdfBytes, password: pdfPassword,
          annotations,
          pages,
          formFieldEdits: new Map(),
          watermark,
          pageNumbers,
          pageImages,
        })
        await triggerDownload(out)
      } catch (err2) {
        console.error(err2)
        alert(t('err.build_pdf', { message: (err2 as Error).message }))
      }
    }
    async function triggerDownload(out: Uint8Array) {
      // A password-protected source stays protected after editing.
      if (pdfPassword) {
        const { protectPdf } = await import('@/utils/loadPdf')
        out = await protectPdf(out, pdfPassword)
      }
      const blob = new Blob([out as BlobPart], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const stem = currentFileName.replace(/\.pdf$/i, '') || 'filled'
      a.download = `${stem}-filled.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoking synchronously can cancel the download in some Safari and
      // Firefox versions; give the browser time to start reading the blob.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
    }
  }, [pdfPages, t])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null
      const typing = t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable
      // Cmd/Ctrl+Z / Cmd/Ctrl+Shift+Z — undo / redo through the history stack
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !typing) {
        e.preventDefault()
        if (e.shiftKey) redo()
        else undo()
        return
      }
      // Cmd/Ctrl+Y as an alternate redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y' && !typing) {
        e.preventDefault()
        redo()
        return
      }
      // Single-key mode shortcuts. Skip when typing or any modifier is held.
      if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const k = e.key.toLowerCase()
        if (k === 't') { e.preventDefault(); setMode(mode === 'text'   ? 'idle' : 'text');   return }
        if (k === 's') { e.preventDefault(); setMode(mode === 'select' ? 'idle' : 'select'); return }
        if (k === 'd') { e.preventDefault(); setMode(mode === 'draw'   ? 'idle' : 'draw');   return }
        if (k === 'e') { e.preventDefault(); setMode(mode === 'edit'   ? 'idle' : 'edit');   return }
        if (k === 'r') { e.preventDefault(); setMode(mode === 'redact' ? 'idle' : 'redact'); return }
        if (k === 'i') {
          e.preventDefault()
          const now = Date.now()
          setPendingTextValue(formatDate(now, undefined))
          setPendingDateMs(now)
          setMode('text')
          return
        }
        if (k === 'p') { e.preventDefault(); setProfileDialogOpen(true); return }
      }
      if (e.key === 'Escape' && !typing && mode !== 'idle') {
        setMode('idle')
      } else if (mode === 'select' && selectedId && !typing && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault()
        removeAnnotation(selectedId)
        setSelectedId(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, selectedId, removeAnnotation, setMode, setSelectedId, undo, redo, setPendingTextValue, setPendingDateMs])

  return (
    <ErrorBoundary strings={{
      title: t('app.error.title'),
      body: t('app.error.body'),
      reload: t('app.error.reload'),
    }}>
    <>
      <Onboarding replayKey={tourReplayKey} />
      <AutoSaveRecentFile />
      <div className="flex h-full">
        {pdfBytes && (
          <Suspense fallback={null}>
            <RecentSidebar
              onPickFile={pickFile}
              onSwitchTo={handleSwitchTo}
              refreshKey={recentRefreshKey}
              open={recentOpen}
              onOpenChange={setRecentOpen}
            />
          </Suspense>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="sticky top-0 z-30">
            {pdfBytes ? (
              <Suspense fallback={<div className="frosted h-14 border-b" />}>
                <Toolbar
                  onOpenFile={handleOpenFile}
                  onMergePdf={handleMergePdf}
                  onOpenSignature={() => setSigModalOpen(true)}
                  onOpenProfile={() => setProfileDialogOpen(true)}
                  onOpenHelp={() => setTourReplayKey((k) => k + 1)}
                  onOpenPages={() => setPagesDialogOpen(true)}
                  onOpenRecent={() => setRecentOpen(true)}
                  onDownload={handleDownload}
                  hasMultiplePages={pdfPages.length > 1}
                  textFamily={textFamily}
                  setTextFamily={setTextFamily}
                  textSize={textSize}
                  setTextSize={setTextSize}
                  textColor={textColor}
                  setTextColor={setTextColor}
                  snapEnabled={snapEnabled}
                  setSnapEnabled={setSnapEnabled}
                  sigModalOpen={sigModalOpen}
                  profileDialogOpen={profileDialogOpen}
                />
                <ModeBanner />
              </Suspense>
            ) : (
              <LandingHeader
                onOpenFile={pickFile}
                onHelp={() => setTourReplayKey((k) => k + 1)}
              />
            )}
          </div>
          <div className="flex min-h-0 flex-1">
            <main id="pdf-main" className="flex-1 overflow-auto bg-muted/30">
              {pdfBytes ? (
                <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t('pdf.loading')}</div>}>
                  <PdfViewer
                    textFamily={textFamily}
                    textSize={textSize}
                    textColor={textColor}
                    snapEnabled={snapEnabled}
                    onPagesLoaded={setPdfPages}
                  />
                </Suspense>
              ) : (
                <EmptyState onFile={handleOpenFile} onRecentFile={handleSwitchTo} />
              )}
            </main>
            {pdfBytes && pdfPages.length > 1 && (
              <Suspense fallback={null}>
                <PdfThumbnailRail
                  pages={pdfPages}
                  onReorder={handleReorderPages}
                  onRotatePage={handleRotatePage}
                  onDeletePage={handleDeletePage}
                />
              </Suspense>
            )}
          </div>
        </div>
        {sigModalOpen && (
          <Suspense fallback={null}>
            <SignatureModal open={sigModalOpen} onOpenChange={setSigModalOpen} />
          </Suspense>
        )}
        {profileDialogOpen && (
          <Suspense fallback={null}>
            <ProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
          </Suspense>
        )}
        {pdfBytes && pdfPages.length > 1 && (
          <Suspense fallback={null}>
            <PdfPagesDialog
              pages={pdfPages}
              open={pagesDialogOpen}
              onOpenChange={setPagesDialogOpen}
              onReorder={handleReorderPages}
              onRotatePage={handleRotatePage}
              onDeletePage={handleDeletePage}
            />
          </Suspense>
        )}
      </div>
    </>
    </ErrorBoundary>
  )
}
